import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

import Product from '@/classes/Product';
import BaseButton from '@/components/BaseButton.vue';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridRow from '@/components/BaseGridRow.vue';
import Checkbox from '@/components/Checkbox.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import CalculatorModelInterface from '@/interfaces/CalculatorModelInterface';
import {
  reactive,
  computed,
  watch,
  ref,
  onBeforeUnmount, WatchStopHandle,
} from 'vue';
import { useRouter } from 'vue-router';
import {
  EnetAPI,
} from '@/api';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import BaseCalculatorUsageField from '@/components/BaseCalculator/BaseCalculatorUsageField.vue';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import { env } from '@/helpers';
import { useStore } from 'vuex';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductCardCalculator',
  props: {
    product: {},
    postcode: {},
    buttonLabel: { default: 'Tarif finden' },
    options: { default: () => ({}) }
  },
  setup(__props: any) {

const props = __props;

const router = useRouter();
const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

const isVisible = computed<boolean>(() => (env('CALCULATOR_ACTIVE') as boolean || [true, 'true', 1].includes((props.forceVisibility || 0))));
const branch = computed<BranchInterface | undefined>(() => store.getters['branches/all'].find((br: BranchInterface) => br.mainCategory.key === props.product.branch));

const valid = reactive({
  postcode: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const hasFocusedOutOnce = reactive({
  postcode: false,
  city: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const form = ref<CalculatorModelInterface>({
  ...{
    productId: props.product.id,
    branch: props.product.branch,
    postcode: undefined,
    city: undefined,
    street: undefined,
    houseNumber: undefined,
    usage: undefined,
    business: false,
  },
  ...props.options,
});

const postcodeLoaded = ref<string | number>('');
const preselectedPostcodeSet = ref(false);
const selectableCities = ref<CityInterface[]>([]);
const selectableStreets = ref<StreetInterface[]>([]);

const showValidCheck = computed(() => ({
  postcode: hasFocusedOutOnce.postcode ? valid.postcode : undefined,
  city: hasFocusedOutOnce.city ? !!form.value.city : undefined,
  street: hasFocusedOutOnce.street ? !!form.value.street : undefined,
  houseNumber: hasFocusedOutOnce.houseNumber ? valid.houseNumber : undefined,
  usage: hasFocusedOutOnce.usage ? valid.usage : undefined,
}));

const isFetching = ref(false);
const fetchCitiesAndStreets = ($postcode: string | number) => {
  if (`${$postcode}`.length === 5 && $postcode !== postcodeLoaded.value) {
    selectableCities.value = [];
    selectableStreets.value = [];
    valid.postcode = false;
    hasFocusedOutOnce.city = false;
    hasFocusedOutOnce.street = false;
    hasFocusedOutOnce.houseNumber = false;
    hasFocusedOutOnce.usage = false;

    postcodeLoaded.value = $postcode as string;

    isFetching.value = true;
    EnetAPI.getCitiesAndStreets($postcode).then((data) => {
      selectableCities.value = data.cities;
      selectableStreets.value = data.streets;
      if (!((props.options.postcode === $postcode) && !preselectedPostcodeSet.value)) {
        form.value.city = selectableCities.value[0].city;
        if (selectableCities.value.length === 1) {
          form.value.city = selectableCities.value[0].city;
          hasFocusedOutOnce.city = true;
        } else {
          form.value.city = undefined;
        }
        if (selectableStreets.value.length === 1) {
          hasFocusedOutOnce.street = true;
          form.value.street = selectableStreets.value[0].street;
        } else {
          form.value.street = undefined;
        }
        preselectedPostcodeSet.value = true;
      }
      valid.postcode = true;
    }).catch(() => {
      valid.postcode = false;
    }).finally(() => {
      isFetching.value = false;
      hasFocusedOutOnce.postcode = true;
    });
  }
};

watch(() => form.value.postcode, ($newPostcode) => {
  fetchCitiesAndStreets($newPostcode as string);
});

if (form.value.postcode && `${form.value.postcode}`.length === 5) {
  fetchCitiesAndStreets(form.value.postcode);
}

const cityOptions = computed(() => [...selectableCities.value].map(($s) => ({
  label: $s.city,
  value: $s.city,
})));

const streetsOptions = computed(() => [...selectableStreets.value].map(($s) => ({
  label: $s.street,
  value: $s.street,
})));

const showCity = computed<boolean>(() => (
  `${form.value.postcode || ''}`.trim().length === 5
    && cityOptions.value.length > 0
));
const showStreet = computed<boolean>(() => (
  showCity.value
    && streetsOptions.value.length > 0
));
const showHouseNumber = computed<boolean>(() => showStreet.value);
const showPostcode = computed<boolean>(() => props.product.isInternet || !!form.value.usage);

const submit = () => {
  let path = '/preisanzeige/';

  if (!form.value.postcode) return;
  if (`${form.value.postcode || ''}`.trim().length !== 5) return;
  if (!form.value.city) return;
  if (!form.value.street) return;
  if (!form.value.houseNumber) return;

  const params: Record<string, string> = {
    stadt: form.value.city as string,
    strasse: form.value.street as string,
    hausnummer: form.value.houseNumber as string,
  };

  if (form.value.business) {
    params.gewerbe = '1';
  }

  if (form.value.productId) {
    params.pid = `${form.value.productId}`;
  }

  const urlParams = new URLSearchParams(params);

  path += `${branch.value?.slug}/${form.value.postcode}/${form.value.usage}/?${urlParams.toString()}`;

  router.push(path);
};

const checkPostcodeValid = () => {
  valid.postcode = `${form.value.postcode || ''}`.trim().length === 5 && selectableCities.value.length > 0;
};

const checkUsageValid = () => {
  valid.usage = parseInt(`${form.value.usage || ''}`, 10) > 0;
};

watch(() => form.value.usage, checkUsageValid);

const checkHousenumberValid = () => {
  valid.houseNumber = `${form.value.houseNumber || ''}`.trim().length >= 1;
};

let callbackWatcher: WatchStopHandle;

onBeforeUnmount(() => {
  if (callbackWatcher) {
    callbackWatcher();
  }
});

return (_ctx: any,_cache: any) => {
  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "product-card-calculator",
        ref_key: "root",
        ref: root
      }, [
        _createVNode(BaseGridRow, { small: "" }, {
          default: _withCtx(() => [
            (!_ctx.product.isInternet)
              ? (_openBlock(), _createBlock(BaseGridCol, { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(BaseCalculatorUsageField, {
                      label: "Dein Verbrauch*",
                      modelValue: form.value.usage,
                      "onUpdate:modelValue": [
                        _cache[0] || (_cache[0] = ($event: any) => ((form.value.usage) = $event)),
                        _cache[1] || (_cache[1] = ($event: any) => (hasFocusedOutOnce.usage = true))
                      ],
                      branch: branch.value.mainCategory.key || 'power',
                      "is-valid": showValidCheck.value.usage,
                      "valid-color": [branch.value.customColor.primary, branch.value.customColor.secondary],
                      "no-initial-value": "",
                      onKeyup: checkUsageValid,
                      onFocusout: _cache[2] || (_cache[2] = () => { checkUsageValid(); hasFocusedOutOnce.usage = true })
                    }, null, 8, ["modelValue", "branch", "is-valid", "valid-color"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (showPostcode.value)
              ? (_openBlock(), _createBlock(BaseGridCol, { key: 1 }, {
                  default: _withCtx(() => [
                    _createVNode(InputField, {
                      label: "Deine Postleitzahl",
                      "only-numeric": true,
                      "error-numeric": "Ungültige Postleitzahl",
                      "max-length": 5,
                      "min-length": 5,
                      "show-loader": isFetching.value,
                      "is-valid": showValidCheck.value.postcode,
                      "valid-color": [branch.value.customColor.primary, branch.value.customColor.secondary],
                      modelValue: form.value.postcode,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.postcode) = $event)),
                      onKeyup: checkPostcodeValid
                    }, null, 8, ["show-loader", "is-valid", "valid-color", "modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (showCity.value)
              ? (_openBlock(), _createBlock(BaseGridCol, { key: 2 }, {
                  default: _withCtx(() => [
                    _createVNode(SelectField, {
                      label: "Deine Stadt*",
                      modelValue: form.value.city,
                      "onUpdate:modelValue": [
                        _cache[4] || (_cache[4] = ($event: any) => ((form.value.city) = $event)),
                        _cache[5] || (_cache[5] = ($event: any) => (hasFocusedOutOnce.city = true))
                      ],
                      readonly: selectableCities.value.length <= 1,
                      options: cityOptions.value,
                      "is-valid": showValidCheck.value.city,
                      "valid-color": [branch.value.customColor.primary, branch.value.customColor.secondary]
                    }, null, 8, ["modelValue", "readonly", "options", "is-valid", "valid-color"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (showStreet.value)
              ? (_openBlock(), _createBlock(BaseGridCol, { key: 3 }, {
                  default: _withCtx(() => [
                    _createVNode(SelectField, {
                      label: "Straße*",
                      modelValue: form.value.street,
                      "onUpdate:modelValue": [
                        _cache[6] || (_cache[6] = ($event: any) => ((form.value.street) = $event)),
                        _cache[7] || (_cache[7] = ($event: any) => (hasFocusedOutOnce.street = true))
                      ],
                      options: streetsOptions.value,
                      search: true,
                      "is-valid": showValidCheck.value.street,
                      "valid-color": [branch.value.customColor.primary, branch.value.customColor.secondary]
                    }, null, 8, ["modelValue", "options", "is-valid", "valid-color"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (showHouseNumber.value)
              ? (_openBlock(), _createBlock(BaseGridCol, { key: 4 }, {
                  default: _withCtx(() => [
                    _createVNode(InputField, {
                      label: "Hausnummer*",
                      type: "text",
                      modelValue: form.value.houseNumber,
                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.value.houseNumber) = $event)),
                      "is-valid": showValidCheck.value.houseNumber,
                      "valid-color": [branch.value.customColor.primary, branch.value.customColor.secondary],
                      onKeyup: checkHousenumberValid,
                      onFocusout: _cache[9] || (_cache[9] = ($event: any) => (hasFocusedOutOnce.houseNumber = true))
                    }, null, 8, ["modelValue", "is-valid", "valid-color"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_ctx.product.isInternet && showHouseNumber.value)
              ? (_openBlock(), _createBlock(BaseGridCol, { key: 5 }, {
                  default: _withCtx(() => [
                    _createVNode(Checkbox, {
                      modelValue: form.value.business,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.value.business) = $event))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Ich bin Geschäftskund:in ")
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(BaseGridCol, null, {
              default: _withCtx(() => [
                _createVNode(BaseButton, {
                  branch: branch.value,
                  width: "full",
                  style: _normalizeStyle({
              background: `linear-gradient(90deg, ${branch.value.customColor.primary}, ${branch.value.customColor.secondary})`,
            }),
                  onClick: _withModifiers(submit, ["prevent"])
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.buttonLabel), 1)
                  ]),
                  _: 1
                }, 8, ["branch", "style"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ], 512))
    : _createCommentVNode("", true)
}
}

})